import { useState } from 'react';

import { ExactProspectPreviewFrame } from '@/components/exact-prospect-preview-frame';
import { ProspectPreviewContentEditor } from '@/components/prospect-preview-content-editor';
import { createProspectPreviewContent } from '@/lib/prospect-preview';
import type { ProspectPreviewContent } from '@/types';
import brandLogo from '../../../assets/images/brand/logo.png';

export function PublicProspectPreviewBuilderPage() {
  const [content, setContent] = useState(() => createProspectPreviewContent());
  const [editorKey, setEditorKey] = useState(0);
  const [message, setMessage] = useState<string | null>(null);

  const updateContent = <Key extends keyof ProspectPreviewContent>(
    key: Key,
    value: ProspectPreviewContent[Key],
  ) => {
    setContent((current) => ({ ...current, [key]: value }));
  };

  const startOver = () => {
    setContent(createProspectPreviewContent());
    setEditorKey((current) => current + 1);
    setMessage(null);
  };

  return (
    <main className="public-builder-page">
      <header className="public-builder-header">
        <a className="public-preview-brand" href="https://everyvisit.app/">
          <img src={brandLogo} alt="" />
          <span>Everyvisit</span>
        </a>
        <a className="button secondary" href="https://everyvisit.app/">Back to website</a>
      </header>

      <section className="public-builder-intro">
        <p className="preview-eyebrow">Try Everyvisit with your business</p>
        <h1>See your business in the app before you sign up.</h1>
        <p>
          Add your details, services, and team. The phone updates instantly and the complete
          booking flow is yours to explore.
        </p>
        <div className="privacy-notice">
          <strong>Private, temporary preview</strong>
          <span>
            Nothing is uploaded or saved. Your images and edits stay in this browser tab and
            disappear when you close or refresh it.
          </span>
        </div>
      </section>

      <div className="public-builder-workspace">
        <section className="public-builder-editor panel" key={editorKey}>
          <div className="panel-heading">
            <div>
              <h2>Build your preview</h2>
              <p>Changes appear on the phone as you type.</p>
            </div>
            <button className="button secondary" type="button" onClick={startOver}>
              Start over
            </button>
          </div>
          <ProspectPreviewContentEditor
            content={content}
            update={updateContent}
            onError={setMessage}
          />
          {message ? (
            <div className="preview-message inline-error" role="alert">{message}</div>
          ) : null}
        </section>

        <aside className="public-builder-preview" aria-label="Interactive business preview">
          <div className="prospect-preview-heading">
            <strong>Your interactive preview</strong>
            <span>Tap services and complete a test booking—nothing is submitted.</span>
          </div>
          <ExactProspectPreviewFrame content={content} />
        </aside>
      </div>
    </main>
  );
}
